import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Search,
  Eye,
  Loader2,
  Clock,
  Building,
  ShieldOff,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useAdminUsers, type AdminUser } from "@/hooks/use-admin-users";
import { toast } from "sonner";

function getInitials(fullName: string | null, email: string): string {
  if (fullName) {
    return fullName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return email[0].toUpperCase();
}

function UserSkeleton() {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-border">
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-44" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-8 w-24 rounded-md" />
      </div>
    </div>
  );
}

interface UserRowProps {
  user: AdminUser;
  currentUserId: string | undefined;
  isSuperAdmin: boolean;
}

function UserRow({ user, currentUserId, isSuperAdmin }: UserRowProps) {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [impersonating, setImpersonating] = useState(false);

  const isSelf = user.id === currentUserId;

  const handleImpersonate = async () => {
    setImpersonating(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "impersonate-user",
        { body: { target_email: user.email } },
      );

      if (error || !data?.hashed_token) {
        toast.error(data?.error ?? "Failed to start impersonation session.");
        return;
      }

      await startImpersonation(user.email, data.hashed_token as string);
      navigate("/dashboard");
    } catch (err) {
      toast.error((err as Error).message || "Failed to start impersonation.");
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-4">
        <Avatar>
          {user.avatar_url && (
            <AvatarImage src={user.avatar_url} alt={user.full_name ?? user.email} />
          )}
          <AvatarFallback className="bg-primary text-primary-foreground text-sm">
            {getInitials(user.full_name, user.email)}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium">
            {user.full_name ?? <span className="text-muted-foreground italic">No name</span>}
          </p>
          <p className="text-xs text-muted-foreground">{user.email}</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          {user.last_sign_in_at
            ? formatDistanceToNow(new Date(user.last_sign_in_at), {
                addSuffix: true,
              })
            : "Never"}
        </div>

        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Building className="w-3 h-3" />
          {user.workspace_count}
        </div>

        {user.email_confirmed_at ? (
          <Badge variant="outline" className="text-xs">
            verified
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">
            unverified
          </Badge>
        )}

        {isSuperAdmin && !isSelf && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleImpersonate}
                disabled={impersonating}
              >
                {impersonating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Eye className="w-3.5 h-3.5" />
                )}
                <span className="ml-1.5">
                  {impersonating ? "Starting..." : "Impersonate"}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>View app as this user</TooltipContent>
          </Tooltip>
        )}

        {isSelf && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-xs text-muted-foreground px-2">
                <ShieldOff className="w-3 h-3" />
                You
              </div>
            </TooltipTrigger>
            <TooltipContent>Cannot impersonate yourself</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { user, syntraAdminRole } = useAuth();
  const isSuperAdmin = syntraAdminRole === "super_admin";
  const [search, setSearch] = useState("");

  const { data: users, isLoading, error } = useAdminUsers();

  const filtered = (users ?? []).filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.full_name ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Users</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {isLoading
            ? "Loading..."
            : `${users?.length ?? 0} user${users?.length === 1 ? "" : "s"} on platform`}
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or email..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <UserSkeleton key={i} />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-destructive py-4 text-center">
              Failed to load users.
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              {search ? "No users match your search." : "No users found."}
            </p>
          ) : (
            <div className="space-y-3">
              {filtered.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  currentUserId={user?.id}
                  isSuperAdmin={isSuperAdmin}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
